import { useState, useEffect, useMemo } from 'react';
import { useAuthFetch } from '../../hooks/useAuthFetch';
import TopicMergeModal from './TopicMergeModal';
import TopicDuplicatesModal from './TopicDuplicatesModal';

const FIELD_LABELS = {
  name: 'Name',
  vendor: 'Vendor',
  examnumber: 'Exam #',
  expected_exam_questions: 'Expected Exam Questions',
  official_url: 'Official URL',
  lab_guide_viable: 'Lab Guide Viable',
};

function EditModal({ topic, onSave, onClose }) {
  const { authFetch } = useAuthFetch();
  const [form, setForm] = useState({
    name: topic.name ?? '',
    vendor: topic.vendor ?? '',
    examnumber: topic.examnumber ?? '',
    expected_exam_questions: topic.expected_exam_questions ?? 0,
    official_url: topic.official_url ?? '',
    lab_guide_viable: topic.lab_guide_viable ?? true,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [similarTopics, setSimilarTopics] = useState([]);

  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  useEffect(() => {
    if (!form.name || form.name.trim() === topic.name) {
      setSimilarTopics([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await authFetch(
          `${process.env.REACT_APP_API_URL}/api/admin/topics/similar?name=${encodeURIComponent(form.name.trim())}`
        );
        if (res.ok) {
          const data = await res.json();
          setSimilarTopics(data.filter(t => t.id !== topic.id));
        }
      } catch (_) { /* ignore */ }
    }, 400);
    return () => clearTimeout(timer);
  }, [form.name]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const body = {
        name: form.name.trim(),
        vendor: form.vendor,
        examnumber: form.examnumber,
        expected_exam_questions: parseInt(form.expected_exam_questions, 10) || 0,
        official_url: form.official_url || null,
        lab_guide_viable: form.lab_guide_viable,
      };
      const res = await authFetch(
        `${process.env.REACT_APP_API_URL}/api/admin/topics/${topic.id}`,
        { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `Server error ${res.status}`);
      }
      const updated = await res.json();
      onSave(updated);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Close on backdrop click
  const handleBackdrop = (e) => { if (e.target === e.currentTarget) onClose(); };

  const inputStyle = {
    width: '100%',
    padding: '0.4rem 0.6rem',
    borderRadius: '6px',
    border: '1px solid var(--color-border-strong)',
    background: 'var(--color-surface)',
    color: 'var(--color-text)',
    fontSize: '0.875rem',
    boxSizing: 'border-box',
  };
  const labelStyle = { fontSize: '0.78rem', fontWeight: 600, color: 'var(--color-text-muted)', marginBottom: '0.25rem', display: 'block' };
  const fieldStyle = { marginBottom: '0.85rem' };

  return (
    <div
      onClick={handleBackdrop}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <div style={{
        background: 'var(--color-surface)', borderRadius: '12px', border: '1px solid var(--color-border)',
        padding: '1.5rem', width: '480px', maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.25rem' }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: '1rem' }}>Edit Topic</div>
            <div style={{ fontSize: '0.78rem', color: 'var(--color-text-subtle)', marginTop: '2px' }}>
              {topic.domain_name} · {topic.group_name}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2rem', color: 'var(--color-text-muted)', lineHeight: 1 }}>✕</button>
        </div>

        <div style={fieldStyle}>
          <label style={labelStyle}>{FIELD_LABELS.name}</label>
          <input style={inputStyle} value={form.name} onChange={e => set('name', e.target.value)} />
          {similarTopics.length > 0 && (
            <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', marginTop: '0.3rem', padding: '0.4rem 0.6rem', borderRadius: '4px', background: 'rgba(249,115,22,0.08)', border: '1px solid rgba(249,115,22,0.25)' }}>
              Similar topics: {similarTopics.map((t, i) => (
                <span key={t.id}>{i > 0 ? ', ' : ''}<strong>{t.name}</strong> ({t.domain_name} › {t.group_name}, {Math.round(t.similarity * 100)}%)</span>
              ))}
            </div>
          )}
        </div>
        <div style={fieldStyle}>
          <label style={labelStyle}>{FIELD_LABELS.vendor}</label>
          <input style={inputStyle} value={form.vendor} onChange={e => set('vendor', e.target.value)} placeholder="e.g. Microsoft" />
        </div>
        <div style={fieldStyle}>
          <label style={labelStyle}>{FIELD_LABELS.examnumber}</label>
          <input style={inputStyle} value={form.examnumber} onChange={e => set('examnumber', e.target.value)} placeholder="e.g. AZ-104" />
        </div>
        <div style={fieldStyle}>
          <label style={labelStyle}>{FIELD_LABELS.expected_exam_questions}</label>
          <input style={inputStyle} type="number" min="0" value={form.expected_exam_questions} onChange={e => set('expected_exam_questions', e.target.value)} />
        </div>
        <div style={fieldStyle}>
          <label style={labelStyle}>{FIELD_LABELS.official_url}</label>
          <input style={inputStyle} value={form.official_url} onChange={e => set('official_url', e.target.value)} placeholder="https://…" />
        </div>
        <div style={{ ...fieldStyle, display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
          <input
            type="checkbox"
            id="lab_guide_viable"
            checked={form.lab_guide_viable}
            onChange={e => set('lab_guide_viable', e.target.checked)}
            style={{ width: 16, height: 16, cursor: 'pointer' }}
          />
          <label htmlFor="lab_guide_viable" style={{ ...labelStyle, marginBottom: 0, cursor: 'pointer' }}>
            {FIELD_LABELS.lab_guide_viable}
          </label>
        </div>

        {error && <p className="costs-error" style={{ marginBottom: '0.75rem' }}>{error}</p>}

        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
          <button onClick={onClose} className="costs-range-btn" disabled={saving}>Cancel</button>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              padding: '0.3rem 1rem', borderRadius: '6px', border: '1px solid rgba(249,115,22,0.55)',
              background: 'var(--color-accent-bg-2)', color: 'var(--color-text)', fontSize: '0.85rem',
              fontWeight: 500, cursor: saving ? 'wait' : 'pointer',
            }}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

const COLUMNS = [
  { key: 'domain_name', label: 'Domain · Group', getValue: t => `${t.domain_name} ${t.group_name}` },
  { key: 'name',                  label: 'Name',           getValue: t => t.name },
  { key: 'vendor',                label: 'Vendor',         getValue: t => t.vendor || '' },
  { key: 'examnumber',            label: 'Exam #',         getValue: t => t.examnumber || '' },
  { key: 'expected_exam_questions', label: 'Expected',        getValue: t => t.expected_exam_questions },
  { key: 'question_count',         label: 'Questions',       getValue: t => t.question_count ?? 0 },
  { key: 'lab_guide_viable',      label: 'Lab Guide',      getValue: t => t.lab_guide_viable ? 1 : 0 },
  { key: 'last_validated',        label: 'Last Validated', getValue: t => t.last_validated ? new Date(t.last_validated).getTime() : -Infinity },
  { key: 'created_at',            label: 'Added',          getValue: t => t.created_at ? new Date(t.created_at).getTime() : -Infinity },
];

export default function TopicsTab() {
  const { authFetch } = useAuthFetch();
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);
  const [merging, setMerging] = useState(null);          // sourceTopic for merge
  const [mergePreload, setMergePreload] = useState(null); // target topic from Find Duplicates
  const [showDuplicates, setShowDuplicates] = useState(false);
  const [sortKey, setSortKey] = useState('domain_name');
  const [sortDir, setSortDir] = useState('asc');
  const [completeOnly, setCompleteOnly] = useState(false);

  useEffect(() => {
    authFetch(`${process.env.REACT_APP_API_URL}/api/admin/topics`)
      .then(res => res.json())
      .then(data => { setTopics(data); setLoading(false); })
      .catch(err => { console.error(err); setError('Failed to load topics.'); setLoading(false); });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    let rows = q
      ? topics.filter(t =>
          t.name.toLowerCase().includes(q) ||
          (t.vendor || '').toLowerCase().includes(q) ||
          (t.domain_name || '').toLowerCase().includes(q) ||
          (t.group_name || '').toLowerCase().includes(q) ||
          (t.examnumber || '').toLowerCase().includes(q)
        )
      : topics;
    if (completeOnly) rows = rows.filter(t => t.expected_exam_questions > 0 && t.question_count >= t.expected_exam_questions * 2);

    const col = COLUMNS.find(c => c.key === sortKey);
    if (!col) return rows;
    return [...rows].sort((a, b) => {
      const av = col.getValue(a);
      const bv = col.getValue(b);
      const cmp = typeof av === 'number'
        ? av - bv
        : String(av).localeCompare(String(bv));
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [topics, search, sortKey, sortDir, completeOnly]);

  const handleSave = (updated) => {
    setTopics(prev => prev.map(t => t.id === updated.id ? { ...t, ...updated } : t));
    setEditing(null);
  };

  const handleMerged = (sourceId, updatedTarget) => {
    setTopics(prev =>
      prev
        .filter(t => t.id !== sourceId)
        .map(t => t.id === updatedTarget.id ? { ...t, ...updatedTarget } : t)
    );
    setMerging(null);
    setMergePreload(null);
  };

  const handleDuplicateMerge = (topicA, topicB) => {
    setMerging(topicA);
    setMergePreload(topicB);
  };

  const SortIcon = ({ colKey }) => {
    if (sortKey !== colKey) return <span style={{ opacity: 0.3, marginLeft: '0.3em' }}>↕</span>;
    return <span style={{ marginLeft: '0.3em' }}>{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  const thStyle = (colKey) => ({
    cursor: 'pointer',
    userSelect: 'none',
    whiteSpace: 'nowrap',
    color: sortKey === colKey ? 'var(--color-text)' : undefined,
  });

  if (loading) return <p className="costs-loading">Loading topics…</p>;
  if (error) return <p className="costs-error">{error}</p>;

  return (
    <div>
      {editing && (
        <EditModal
          topic={editing}
          onSave={handleSave}
          onClose={() => setEditing(null)}
        />
      )}
      {showDuplicates && (
        <TopicDuplicatesModal
          onClose={() => setShowDuplicates(false)}
          onMerge={handleDuplicateMerge}
        />
      )}
      {merging && (
        <TopicMergeModal
          sourceTopic={merging}
          allTopics={topics}
          preloadTargetId={mergePreload?.id}
          onClose={() => { setMerging(null); setMergePreload(null); }}
          onMerged={handleMerged}
        />
      )}

      <div className="costs-section">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
          <p className="costs-section-title" style={{ margin: 0 }}>Topics ({filtered.length}{filtered.length !== topics.length ? ` of ${topics.length}` : ''})</p>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            <button
              onClick={() => setCompleteOnly(v => !v)}
              className="costs-range-btn"
              style={{
                background: completeOnly ? 'var(--color-accent-bg-2)' : undefined,
                color: completeOnly ? 'var(--color-text)' : undefined,
                borderColor: completeOnly ? 'rgba(249,115,22,0.55)' : undefined,
              }}
            >
              Complete
            </button>
            <button
              onClick={() => setShowDuplicates(true)}
              style={{ padding: '0.3rem 0.9rem', borderRadius: '6px', fontSize: '0.82rem', fontWeight: 500, border: '1px solid var(--color-border-strong)', background: 'var(--color-surface)', color: 'var(--color-text-muted)', cursor: 'pointer' }}
            >
              Find Duplicates
            </button>
            <input
              type="search"
              placeholder="Search…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{
                padding: '0.3rem 0.7rem', borderRadius: '6px', fontSize: '0.85rem',
                border: '1px solid var(--color-border-strong)', background: 'var(--color-surface)',
                color: 'var(--color-text)', width: '220px',
              }}
            />
          </div>
        </div>

        <div className="costs-table-wrapper">
          <table className="costs-table">
            <thead>
              <tr>
                {COLUMNS.map(col => (
                  <th key={col.key} style={thStyle(col.key)} onClick={() => handleSort(col.key)}>
                    {col.label}<SortIcon colKey={col.key} />
                  </th>
                ))}
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(topic => (
                <tr key={topic.id}>
                  <td style={{ fontSize: '0.78rem', color: 'var(--color-text-subtle)', whiteSpace: 'nowrap' }}>
                    {topic.domain_name}<br />{topic.group_name}
                  </td>
                  <td style={{ fontWeight: 500, maxWidth: 200 }}>{topic.name}</td>
                  <td style={{ color: 'var(--color-text-muted)' }}>{topic.vendor || '—'}</td>
                  <td style={{ whiteSpace: 'nowrap', color: 'var(--color-text-muted)' }}>{topic.examnumber || '—'}</td>
                  <td style={{ textAlign: 'center' }}>{topic.expected_exam_questions}</td>
                  <td style={{ textAlign: 'center', color: topic.expected_exam_questions > 0 && topic.question_count >= topic.expected_exam_questions * 2 ? 'var(--color-accent)' : 'var(--color-text-muted)' }}>{topic.question_count ?? 0}</td>
                  <td style={{ textAlign: 'center' }}>
                    <span style={{ color: topic.lab_guide_viable ? 'var(--color-accent)' : 'var(--color-text-subtle)' }}>
                      {topic.lab_guide_viable ? '✓' : '✗'}
                    </span>
                  </td>
                  <td style={{ fontSize: '0.78rem', color: 'var(--color-text-subtle)', whiteSpace: 'nowrap' }}>
                    {topic.last_validated ? new Date(topic.last_validated).toLocaleDateString() : '—'}
                  </td>
                  <td style={{ fontSize: '0.78rem', color: 'var(--color-text-subtle)', whiteSpace: 'nowrap' }}>
                    {topic.created_at ? new Date(topic.created_at).toLocaleDateString() : '—'}
                  </td>
                  <td>
                    <button
                      onClick={() => setEditing({ ...topic })}
                      style={{
                        padding: '2px 10px', borderRadius: '6px', fontSize: '0.78rem', fontWeight: 500,
                        border: '1px solid var(--color-border-strong)', background: 'var(--color-surface)',
                        color: 'var(--color-text-muted)', cursor: 'pointer',
                      }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => setMerging({ ...topic })}
                      style={{ padding: '2px 10px', borderRadius: '6px', fontSize: '0.78rem', fontWeight: 500, border: '1px solid var(--color-border-strong)', background: 'var(--color-surface)', color: 'var(--color-text-muted)', cursor: 'pointer', marginLeft: '0.4rem' }}
                    >
                      Merge
                    </button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={10} style={{ textAlign: 'center', color: 'var(--color-text-subtle)', padding: '1.5rem' }}>No topics match your search.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
